import { captureException } from '@sentry/react-router'
import { type ReactElement, useEffect } from 'react'
import {
	type ErrorResponse,
	Link,
	isRouteErrorResponse,
	useParams,
	useRouteError,
} from 'react-router'
import { getErrorMessage } from '#app/utils/misc'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => ReactElement | null

const STATUS_MESSAGES: Record<number, { title: string; description: string }> =
	{
		400: {
			title: 'Bad Request',
			description:
				'The request could not be processed. Please check your input and try again.',
		},
		401: {
			title: 'Unauthorized',
			description: 'You need to be logged in to access this page.',
		},
		403: {
			title: 'Access Denied',
			description: "You don't have permission to view this page.",
		},
		404: {
			title: 'Page Not Found',
			description:
				"We couldn't find the page you're looking for. It may have been moved or doesn't exist.",
		},
		500: {
			title: 'Server Error',
			description:
				'Something went wrong on our end. Please try again in a moment.',
		},
	}

function ErrorLayout({
	title,
	description,
	statusCode,
	children,
}: {
	title: string
	description: string
	statusCode?: number
	children?: React.ReactNode
}) {
	return (
		<div className="flex min-h-[60vh] flex-col items-center justify-center px-6 py-16 text-center">
			{statusCode && (
				<span className="mb-4 text-7xl font-bold text-[#E5E7EB]">
					{statusCode}
				</span>
			)}
			<h1 className="mb-3 text-2xl font-semibold text-[#32302F]">{title}</h1>
			<p className="mb-8 max-w-md text-[15px] leading-relaxed text-[#747474]">
				{description}
			</p>
			<div className="flex items-center gap-3">
				<Link
					to="/"
					className="inline-flex h-10 items-center rounded-lg bg-[#32302F] px-5 text-sm font-medium text-white transition-colors hover:bg-[#4a4847]"
				>
					Go Home
				</Link>
				<button
					type="button"
					onClick={() => window.location.reload()}
					className="inline-flex h-10 items-center rounded-lg border border-[#E5E7EB] bg-white px-5 text-sm font-medium text-[#32302F] transition-colors hover:bg-[#F9FAFB]"
				>
					Try Again
				</button>
			</div>
			{children}
		</div>
	)
}

export function GeneralErrorBoundary({
	defaultStatusHandler,
	statusHandlers,
	unexpectedErrorHandler,
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => ReactElement | null
}) {
	const error = useRouteError()
	const params = useParams()
	const isResponse = isRouteErrorResponse(error)
	const isDev =
		typeof window !== 'undefined' && window.ENV?.MODE === 'development'

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	useEffect(() => {
		if (isResponse) return

		captureException(error)
	}, [error, isResponse])

	if (isResponse) {
		const handler = statusHandlers?.[error.status] ?? defaultStatusHandler
		if (handler) {
			return (
				<div className="flex min-h-[60vh] items-center justify-center p-6">
					{handler({ error, params })}
				</div>
			)
		}

		const info = STATUS_MESSAGES[error.status] ?? {
			title: `Error ${error.status}`,
			description:
				typeof error.data === 'string' && error.data
					? error.data
					: 'An unexpected error occurred.',
		}

		return (
			<ErrorLayout
				title={info.title}
				description={info.description}
				statusCode={error.status}
			/>
		)
	}

	if (unexpectedErrorHandler) {
		return (
			<div className="flex min-h-[60vh] items-center justify-center p-6">
				{unexpectedErrorHandler(error)}
			</div>
		)
	}

	return (
		<ErrorLayout
			title="Something went wrong"
			description="An unexpected error occurred. Our team has been notified and we're working to fix it."
		>
			{isDev && (
				<details className="mt-6 w-full max-w-xl text-left">
					<summary className="cursor-pointer text-xs font-medium text-[#9CA3AF] hover:text-[#6B7280]">
						Error details (dev only)
					</summary>
					<pre className="mt-2 max-h-48 overflow-auto rounded-lg bg-[#F9FAFB] p-4 text-xs text-[#6B7280]">
						{getErrorMessage(error)}
					</pre>
				</details>
			)}
		</ErrorLayout>
	)
}
